import React, { useMemo, useState } from "react";
import { AttributionControl, MapContainer, TileLayer } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import HistoryIcon from "@mui/icons-material/History";
import LayersIcon from "@mui/icons-material/Layers";
import Tooltip from "@mui/material/Tooltip";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import MarkerComponent from "./Marker.component";
import CircularProgress from "@mui/material/CircularProgress";
import MapControlComponent from "./Map-control.component";
import TrackComponent from "./Track-component";
import useDictionary from "@ui/hooks/useDictionary";
import DotMarkerComponent from "./Dot-marker.component";
import { IStoredDevice } from "@store/Devices.store";
import { getTrack, sortTrack } from "./track.utils";
import { IProps } from "./IProps";

type markerProps = Omit<IStoredDevice, "groupId" | "type" | "deviceType" | "additionalInfo">;

type MapSource = "osm" | "google" |  "yandex";

interface MapLayerConfig {
  label: string;
  url: string;
  attribution: string;
  maxZoom?: number;
  subdomains?: string[];
}

const MAP_LAYERS: Record<MapSource, MapLayerConfig> = {
  osm: {
    label: "OSM",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  google: {
    label: "GOOGLE",
    url: "http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
    attribution: '&copy; <a href="https://www.google.com/maps">Google</a>',
    maxZoom: 20,
    subdomains: ["mt0", "mt1", "mt2", "mt3"],
  },
  yandex: {
    label: "YANDEX",
    url: "https://tiles.api-maps.yandex.ru/v1/tiles/?projection=web_mercator&x={x}&y={y}&z={z}&lang=ru_RU&l=map&apikey=3b00445b-ba5f-458b-8fa2-470af0109df7",
    attribution: '&copy; <a href="https://yandex.ru/maps/">Yandex</a>',
  },
};

const Component: React.FC<IProps> = ({
  loading,
  zoom,
  page,
  reload,
  coordsList,
  cookies,
  position,
  isSidebarHidden,
  timezone,
  isTelemetryVisible,
  highlightedDevice,
  highlightedCoords,
  highlightedDate,
  lang,
  trackMode,
  lastCoordsList,
  historyCoordsList,
  lastBounds,
  onMapDrag,
  onMapMove,
  onSideBarBtnClick,
  onReload,
  onMapZoom,
  onDeviceTelemetryOpen,
}: IProps) => {
  const [mapSource, setMapSource] = useState<MapSource>("osm");
  const getWord = useDictionary();
  const isHistory = page === "history" || page === "support";
  const isShortHeight = isHistory && isTelemetryVisible;
  const isTrack = trackMode && isHistory;
  const mapWrapClass = isShortHeight ? "map__wrap" : "map__wrap_fullheight";
  const mapOverlayClass = isShortHeight ? "map__overlay" : "map__overlay_fullheight";
  const sideBarTooltipText = isSidebarHidden ? getWord("showSideBar") : getWord("hideSideBar");
  const telemetryTooltipText = isTelemetryVisible
    ? getWord("hideTelemetry")
    : getWord("showTelemetry");

  const getMarkers = (
    {
      imei,
      name,
      icon,
      gpsCoord,
      lbsCoord,
      date,
      gpsDate,
      lbsDate,
      speed,
      dir,
      alt,
      sats,
      hdop,
      batteryVoltage,
      event,
      temperature,
      cur_sim,
      signal,
    }: markerProps,
    i: number,
  ) => {
    const props = {
      icon,
      gpsCoord,
      lbsCoord,
      imei,
      name,
      date,
      gpsDate,
      lbsDate,
      timezone,
      key: i,
      speed,
      dir,
      alt,
      sats,
      hdop,
      batteryVoltage,
      highlightedDevice,
      highlightedCoords,
      highlightedDate,
      event,
      temperature,
      cur_sim,
      signal,
      lang,
    };
    return <MarkerComponent {...props} />;
  };

  const getDotMarkers = (
    {
      imei,
      name,
      gpsCoord,
      lbsCoord,
      date,
      gpsDate,
      lbsDate,
      speed,
      dir,
      alt,
      sats,
      hdop,
      batteryVoltage,
      event,
      temperature,
      cur_sim,
      signal,
    }: markerProps,
    i: number,
  ) => {
    const props = {
      gpsCoord,
      lbsCoord,
      imei,
      name,
      date,
      gpsDate,
      lbsDate,
      timezone,
      key: i,
      speed,
      dir,
      alt,
      sats,
      hdop,
      batteryVoltage,
      highlightedDevice,
      highlightedCoords,
      highlightedDate,
      event,
      temperature,
      cur_sim,
      signal,
      lang,
    };
    return <DotMarkerComponent {...props} />;
  };
  const track = useMemo(() => {
    if (isHistory) return getTrack(historyCoordsList.sort(sortTrack));
    else return [];
  }, [coordsList]);
  const historyMarkers = useMemo(
    () => historyCoordsList.map(isTrack ? getDotMarkers : getMarkers),
    [historyCoordsList, timezone, highlightedCoords, highlightedDate, lang, isTrack],
  );
  const lastMarkers = useMemo(() => {
    return lastCoordsList.map(getMarkers);
  }, [lastCoordsList, page, historyCoordsList, timezone, highlightedDevice, lang]);
  const mapLayer = MAP_LAYERS[mapSource];

  return (
    <Box className={mapWrapClass} component="section">
      <MapContainer className="map" attributionControl={false} center={position} zoom={zoom}>
        <AttributionControl prefix={false} />
        <TileLayer
          key={mapSource}
          attribution={mapLayer.attribution}
          url={mapLayer.url}
          {...(mapLayer.maxZoom ? { maxZoom: mapLayer.maxZoom } : {})}
          {...(mapLayer.subdomains ? { subdomains: mapLayer.subdomains } : {})}
        />
        <MapControlComponent
          bounds={lastBounds}
          position={position}
          onMapDrag={onMapDrag}
          reload={reload}
          zoom={zoom}
          onReload={onReload}
          onMapZoom={onMapZoom}
          onMapMove={onMapMove}
        />
        {isTrack && <TrackComponent coords={historyCoordsList} />}
        {!isTrack &&
          isHistory && (
            // @ts-ignore
            <MarkerClusterGroup disableClusteringAtZoom={18} removeOutsideVisibleBounds={true}>
              {historyMarkers}
            </MarkerClusterGroup>
          )}
        {isTrack && isHistory && historyMarkers}
        {lastMarkers.length > 1 ? (
          // @ts-ignore
          <MarkerClusterGroup disableClusteringAtZoom={18} removeOutsideVisibleBounds={true}>
            {lastMarkers}
          </MarkerClusterGroup>
        ) : (
          lastMarkers
        )}
      </MapContainer>
      <Box className="map__layers-control">
        <Fab
          className="map__layers-btn"
          size="small"
          aria-label="Выбрать источник карты"
          title="Источник карты"
        >
          <LayersIcon />
        </Fab>
        <Paper className="map__layers-menu" elevation={4}>
          <RadioGroup
            aria-label="Источник карты"
            value={mapSource}
            onChange={(_, value) => setMapSource(value as MapSource)}
          >
            {(Object.keys(MAP_LAYERS) as MapSource[]).map((source) => (
              <FormControlLabel
                key={source}
                value={source}
                control={<Radio size="small" />}
                label={MAP_LAYERS[source].label}
              />
            ))}
          </RadioGroup>
        </Paper>
      </Box>
      <Box
        className={cookies && !isTelemetryVisible ? "map__btn-bar_with-cookies" : "map__btn-bar"}
      >
        <Tooltip
          classes={{
            popper: "map__tooltip-popper",
            tooltip: "map__tooltip",
          }}
          title={sideBarTooltipText}
          placement="top"
        >
          <Fab className="map__btn_sidebar" onClick={onSideBarBtnClick}>
            <ViewSidebarIcon className="map__ico" />
          </Fab>
        </Tooltip>
        {isHistory && (
          <Tooltip
            classes={{
              popper: "map__tooltip-popper",
              tooltip: "map__tooltip",
            }}
            title={telemetryTooltipText}
            placement="top"
          >
            <Fab className="map__btn" onClick={onDeviceTelemetryOpen}>
              <HistoryIcon />
            </Fab>
          </Tooltip>
        )}
      </Box>
      {loading && (
        <Box className={mapOverlayClass}>
          <CircularProgress className="map__loader" size={70} />
        </Box>
      )}
    </Box>
  );
};

const MapComponent = React.memo(Component);

export default MapComponent;
